/**
 * Editing a submission whose fields live in field groups (SkincAIr NTD eCRF).
 *
 * The update endpoint REPLACES the submission's field groups wholesale: any stored
 * group record missing from `fieldGroups` is soft-deleted. The edit form shows one
 * section at a time, so the payload has to list every stored group instance. That
 * rule lives here.
 *
 * Records are the opposite: the endpoint only touches the records it is sent, and
 * it rewrites each one as `JSON.stringify(value)`, logging a change whenever that
 * differs from the stored text. Stored text is jsonb-shaped (keys reordered,
 * spaced), so re-sending an untouched value logs a phantom change and rewrites the
 * row (and a record sent without its `nonce` loses it). So only records the
 * clinician actually changed are sent.
 *
 * Two storage shapes coexist (both written by the Flutter app):
 *  - grouped: every group — repeating or not — is a group record;
 *  - flat:    fields of a NON-repeating group are ungrouped surveyRecords; only
 *             repeating groups and subforms are group records.
 * A flat-stored group is edited as one instance flagged `storedFlat` and goes back
 * as ungrouped records, so an edit never changes how the app stored it.
 */
import { isEqual } from 'lodash';
import { defaultEmptyFieldValue, optionValues, parseStoredFieldValue } from './fieldValueCoercion';

export interface EditRecord {
    surveyField: any;
    value: unknown;
    nonce?: string;
    /** the submission had a record for this field; unset ones are only sent once filled */
    stored?: boolean;
    /** the value as hydrated from the submission; a stored record is sent only once it differs */
    storedValue?: unknown;
}

export interface EditInstance {
    instanceNumber: number;
    groupRecordId?: number;
    storedFlat?: boolean;
    surveyRecords: EditRecord[];
}

export interface EditSubform {
    fieldGroupId: number;
    fieldGroupUuid?: string;
    triggerOptionId: number;
    instances: EditInstance[];
}

export interface FieldGroupEditState {
    /** keyed by schema field-group id; only groups rendered as groups (not subforms) */
    instances: Record<number, EditInstance[]>;
    /** keyed by trigger option id */
    subforms: Record<string, EditSubform>;
}

export interface MissingRequiredField {
    sectionName?: string;
    groupId: number;
    groupName: string;
    /** 1-based position among the group's instances, only for repeating groups */
    instanceIndex?: number;
    instanceNumber: number;
    fieldId: number;
    fieldLabel: string;
    /** label of the multi-option field whose selected option opened this subform */
    subformOf?: string;
    triggerOptionId?: number;
}

export function isSubformGroup(group: any): boolean {
    return group?.displayMode === 'subform';
}

/**
 * SKIN-444 — a repeating group the server has closed to new instances (`maxRepeats: 0`), e.g.
 * the NTD eCRF's visit-level `D1 — Diagnosis` once the diagnosis moved into each lesion.
 * Recorded instances are shown and editable as collected; with none there is nothing to render.
 */
export function isClosedGroup(group: { repeats?: boolean; maxRepeats?: number | null } | null | undefined): boolean {
    return Boolean(group?.repeats) && group?.maxRepeats === 0;
}

/** Options (across the given groups) that open a subform, keyed by option id. */
function subformTriggers(groups: any[]): Map<number, { option: any; parentField: any; subformGroup: any }> {
    const triggers = new Map();
    for (const group of groups) {
        for (const field of group.fields || []) {
            for (const option of field.options || []) {
                if (option?.subformGroup) {
                    triggers.set(option.id, { option, parentField: field, subformGroup: option.subformGroup });
                }
            }
        }
    }
    return triggers;
}

function recordFor(field: any, stored: any): EditRecord {
    const record: EditRecord = {
        surveyField: { ...field, ...(stored?.surveyField || {}), id: field.id },
        value: parseStoredFieldValue(stored?.value ?? defaultEmptyFieldValue(field.type), field.type),
    };
    if (stored?.nonce) record.nonce = stored.nonce;
    if (stored) {
        record.stored = true;
        record.storedValue = record.value;
    }
    return record;
}

function sortedFields(group: any): any[] {
    return [...(group.fields || [])].sort((a, b) => a.order - b.order);
}

function normalizeInstance(group: any, stored: any): EditInstance {
    const byField = new Map((stored.surveyRecords || []).map((r: any) => [r.surveyField?.id, r]));
    return {
        instanceNumber: stored.instanceNumber,
        groupRecordId: stored.groupRecordId,
        surveyRecords: sortedFields(group).map(field => recordFor(field, byField.get(field.id))),
    };
}

/** A new instance with an unset record for every field of the group. */
export function emptyInstance(group: any, instanceNumber: number): EditInstance {
    return { instanceNumber, surveyRecords: sortedFields(group).map(field => recordFor(field, undefined)) };
}

/**
 * Edit state for `groups`. A group with nothing stored starts with its `minRepeats`
 * empty instances; with none required it has no entry and is not sent until the
 * clinician adds one.
 */
export function hydrateFieldGroupEdit(groups: any[], initData: any): FieldGroupEditState {
    const state: FieldGroupEditState = { instances: {}, subforms: {} };
    const storedGroups: any[] = initData?.fieldGroups || [];
    const flatById = new Map((initData?.surveyRecords || []).map((r: any) => [r.surveyField?.id, r]));

    for (const group of groups) {
        if (isSubformGroup(group)) continue;
        const stored = storedGroups.find(sg => sg.fieldGroupId === group.id && !sg.triggerOptionId);
        if (stored) {
            state.instances[group.id] = (stored.instances || []).map((inst: any) => normalizeInstance(group, inst));
            continue;
        }
        const hasFlatValues = (group.fields || []).some((f: any) => flatById.has(f.id));
        if (!group.repeats && hasFlatValues) {
            state.instances[group.id] = [{
                instanceNumber: 0,
                storedFlat: true,
                surveyRecords: sortedFields(group).map(field => recordFor(field, flatById.get(field.id))),
            }];
        } else if (group.minRepeats > 0) {
            state.instances[group.id] = Array.from({ length: group.minRepeats }, (_, i) => emptyInstance(group, i));
        }
    }

    for (const [optionId, { subformGroup }] of Array.from(subformTriggers(groups))) {
        const stored = storedGroups.find(sg => sg.triggerOptionId === optionId)
            // lesion-level subforms written before trigger options existed carry none
            || storedGroups.find(sg => !sg.triggerOptionId && sg.fieldGroupId === subformGroup.id);
        state.subforms[String(optionId)] = {
            fieldGroupId: subformGroup.id,
            fieldGroupUuid: subformGroup.uuid,
            triggerOptionId: optionId,
            instances: stored
                ? (stored.instances || []).map((inst: any) => normalizeInstance(subformGroup, inst))
                : [],
        };
    }
    return state;
}


// ---------------------------------------------------------------- visibility

export type ControllerLookup = (fieldName: string) => { known: boolean; value: unknown };

export function selectedOptionValues(value: unknown): string[] {
    const parsed = typeof value === 'string' ? parseStoredFieldValue(value, undefined) : value;
    if (Array.isArray(parsed)) {
        return parsed
            .map((entry: any) => (entry && typeof entry === 'object' ? entry.multioption : entry))
            .filter((v: unknown): v is string => typeof v === 'string');
    }
    if (parsed === null || parsed === undefined || parsed === '') return [];
    return [decodeOtherValue(parsed).value];
}

/**
 * `conditionalOn: {field, values}` — shown when the controlling field (a sibling in
 * the same group) holds one of `values`. A controller the web cannot read (not in
 * the group, or encrypted like `sex`) never hides a field: hiding it would make a
 * stored value uneditable, which is worse than showing a field that may not apply.
 */
export function isFieldVisible(field: any, lookup: ControllerLookup): boolean {
    const condition = field?.conditionalOn;
    if (!condition?.field || !Array.isArray(condition.values)) return true;
    const controller = lookup(condition.field);
    if (!controller.known) return true;
    return selectedOptionValues(controller.value).some(v => condition.values.includes(v));
}

export function instanceLookup(instance: EditInstance): ControllerLookup {
    return (name: string) => {
        const record = instance.surveyRecords.find(r => r.surveyField?.name === name);
        if (!record) return { known: false, value: undefined };
        return { known: !record.surveyField?.encrypted, value: record.value };
    };
}

/**
 * The one rule for "the web shows and edits this field": not encrypted (those
 * round-trip untouched), and visible under its `conditionalOn`. The form renders
 * exactly these and the required-fields check looks at exactly these.
 */
export function isEditable(field: any, instance: EditInstance): boolean {
    return !field.encrypted && isFieldVisible(field, instanceLookup(instance));
}

export function recordValue(instance: EditInstance, fieldId: number): unknown {
    return instance.surveyRecords.find(r => r.surveyField.id === fieldId)?.value;
}

export interface ActiveSubform {
    option: any;
    subform: EditSubform;
    /** the instance being edited; an empty one when the option was just selected */
    instance: EditInstance;
}

/**
 * Subforms opened by the options selected in `value` of a multioption_with_subforms
 * field. `subforms` comes from hydrateFieldGroupEdit, which has an entry for every
 * trigger option of the groups it was given.
 */
export function activeSubforms(field: any, value: unknown, subforms: Record<string, EditSubform>): ActiveSubform[] {
    const chosen = selectedOptionValues(value);
    return (field.options || [])
        .filter((option: any) => option?.subformGroup && chosen.includes(option.value))
        .map((option: any) => {
            const subform = subforms[String(option.id)];
            return { option, subform, instance: subform.instances[0] ?? emptyInstance(option.subformGroup, 0) };
        });
}

// ------------------------------------------------------- "other" option values

/** select/chips/radio store "other" as the JSON string {"value","otherText"}. */
export function decodeOtherValue(value: unknown): { value: string; otherText?: string } {
    if (value && typeof value === 'object' && 'value' in (value as any)) {
        const v = value as any;
        return v.otherText === undefined ? { value: String(v.value) } : { value: String(v.value), otherText: v.otherText };
    }
    if (typeof value === 'string' && value.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && 'value' in parsed) return decodeOtherValue(parsed);
        } catch { /* a plain string that starts with "{" */ }
    }
    return { value: value === null || value === undefined ? '' : String(value) };
}

export function encodeOtherValue(value: string, otherText: string | undefined, otherOptions: string[] | undefined): string {
    if (otherOptions?.includes(value) && otherText !== undefined) {
        return JSON.stringify({ value, otherText });
    }
    return value;
}

// ------------------------------------------------------------------- payload

/**
 * Only an edit is sent. A stored record goes back once its value differs from what
 * was hydrated (compared structurally: key order is not a change); a field the
 * submission never had goes back once filled — sent empty, the API would create an
 * empty record and log a phantom "null → ''" change (and an empty ungrouped
 * multioption makes it answer 500).
 */
function isSendable(record: EditRecord): boolean {
    return record.stored ? !isEqual(record.value, record.storedValue) : !isEmptyValue(record.value);
}

function outgoingRecords(records: EditRecord[]) {
    return records.filter(isSendable).map(outgoingRecord);
}

/**
 * Ungrouped array values the API's checkValidData accepts as arrays: the smart
 * `multioption`, `multioption_with_subforms` and `text_list`. Any other array-valued
 * ungrouped record (`icd11-code`) makes it answer 400 "Bad Data" — unless an earlier
 * multioption record short-circuits the check to true (reported in SKIN-536). Those
 * are sent as the JSON string the API would store anyway, so the stored value is
 * byte-identical and no phantom change is logged.
 */
const UNGROUPED_ARRAY_TYPES = new Set(['multioption', 'multioption_with_subforms', 'text_list']);

function outgoingUngroupedRecord(record: EditRecord) {
    const out = outgoingRecord(record);
    if (Array.isArray(out.value) && !UNGROUPED_ARRAY_TYPES.has(record.surveyField?.type)) {
        out.value = JSON.stringify(out.value);
    }
    return out;
}

function outgoingRecord(record: EditRecord) {
    const out: any = { surveyField: record.surveyField, value: record.value };
    if (record.nonce) out.nonce = record.nonce;
    return out;
}

function outgoingInstance(instance: EditInstance) {
    const out: any = { instanceNumber: instance.instanceNumber, surveyRecords: outgoingRecords(instance.surveyRecords) };
    if (instance.groupRecordId) out.groupRecordId = instance.groupRecordId;
    return out;
}

/** A stored group the section did not render: listed (so it is kept), records untouched. */
function storedGroupKept(stored: any) {
    const out: any = {
        fieldGroupId: stored.fieldGroupId,
        fieldGroupUuid: stored.fieldGroupUuid,
        instances: (stored.instances || []).map((inst: any) => ({
            instanceNumber: inst.instanceNumber,
            groupRecordId: inst.groupRecordId,
            surveyRecords: [],
        })),
    };
    if (stored.triggerOptionId) out.triggerOptionId = stored.triggerOptionId;
    return out;
}

/**
 * `renderedGroups` are the groups the form showed (one section); `state` their
 * edited values. Returns the ungrouped records and the COMPLETE fieldGroups list.
 */
export function buildFieldGroupEditPayload(renderedGroups: any[], state: FieldGroupEditState, initData: any) {
    const surveyRecords: any[] = [];
    const fieldGroups: any[] = [];
    const handledGroupIds = new Set<number>();
    const handledTriggers = new Set<number>();
    const selected = new Set<number>();

    for (const group of renderedGroups) {
        if (isSubformGroup(group)) continue;
        const instances = state.instances[group.id];
        if (instances === undefined) continue;
        handledGroupIds.add(group.id);
        const flat = instances.filter(i => i.storedFlat);
        flat.forEach(inst => surveyRecords.push(...inst.surveyRecords.filter(isSendable).map(outgoingUngroupedRecord)));
        const grouped = instances.filter(i => !i.storedFlat);
        if (grouped.length > 0 || flat.length === 0) {
            fieldGroups.push({ fieldGroupId: group.id, fieldGroupUuid: group.uuid, instances: grouped.map(outgoingInstance) });
        }
        // Deliberately NOT filtered by isEditable: a subform whose parent field is
        // currently hidden still holds stored data, and leaving it out of the payload
        // would make the API delete it.
        for (const inst of instances) {
            for (const field of group.fields || []) {
                activeSubforms(field, recordValue(inst, field.id), state.subforms).forEach(({ option }) => selected.add(option.id));
            }
        }
    }

    for (const [optionId, { subformGroup }] of Array.from(subformTriggers(renderedGroups))) {
        handledTriggers.add(optionId);
        handledGroupIds.add(subformGroup.id);
        const subform = state.subforms[String(optionId)];
        if (!selected.has(optionId) || !subform || subform.instances.length === 0) continue;
        fieldGroups.push({
            fieldGroupId: subform.fieldGroupId,
            fieldGroupUuid: subform.fieldGroupUuid,
            triggerOptionId: subform.triggerOptionId,
            instances: subform.instances.map(outgoingInstance),
        });
    }

    // Every stored group this section did not render is listed, or the API deletes it.
    for (const stored of initData?.fieldGroups || []) {
        const owned = stored.triggerOptionId
            ? handledTriggers.has(stored.triggerOptionId)
            : handledGroupIds.has(stored.fieldGroupId);
        if (!owned) fieldGroups.push(storedGroupKept(stored));
    }

    return { surveyRecords, fieldGroups };
}

// ---------------------------------------------------------- required fields

function isEmptyValue(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string' && value.trim().startsWith('[')) {
        const parsed = parseStoredFieldValue(value, 'multioption');
        return Array.isArray(parsed) && parsed.length === 0;
    }
    return false;
}

/**
 * SKIN-637 — an "other" answer with a blank note, on a field that declares
 * `otherTextRequired`. The app judges every `otherOptions` field this way; the web and
 * the server only where declared, so historical "Other" answers stored with an empty
 * note elsewhere stay closeable.
 */
function lacksRequiredOtherText(field: any, value: unknown): boolean {
    if (field?.otherTextRequired !== true || isEmptyValue(value)) return false;
    const decoded = decodeOtherValue(value);
    if (!(field.otherOptions ?? []).includes(decoded.value)) return false;
    return (decoded.otherText ?? '').trim() === '';
}

function isMissing(field: any, instance: EditInstance): boolean {
    const value = recordValue(instance, field.id);
    return !!field.required && isEditable(field, instance) && (isEmptyValue(value) || lacksRequiredOtherText(field, value));
}

/**
 * DOM id of a rendered field-group field (a subform field also carries its trigger
 * option), so a missing required field can be jumped to from the modal.
 */
export function fieldAnchorId(groupId: number, instanceNumber: number, fieldId: number, triggerOptionId?: number): string {
    return `fg-field-${groupId}-${instanceNumber}-${fieldId}${triggerOptionId ? `-option-${triggerOptionId}` : ''}`;
}

export function missingFieldAnchorId(field: MissingRequiredField): string {
    return fieldAnchorId(field.groupId, field.instanceNumber, field.fieldId, field.triggerOptionId);
}

/** Visible, editable, required fields left empty — in section/group/field order. */
export function findMissingRequiredFields(sections: any[], state: FieldGroupEditState): MissingRequiredField[] {
    const missing: MissingRequiredField[] = [];
    const orderedSections = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const section of orderedSections) {
        const groups = [...(section.fieldGroups || [])]
            .filter(g => !isSubformGroup(g) && g.isActive !== false)
            .sort((a, b) => a.order - b.order);
        for (const group of groups) {
            const instances = [...(state.instances[group.id] || [])].sort((a, b) => a.instanceNumber - b.instanceNumber);
            instances.forEach((instance, index) => {
                const context = {
                    sectionName: section.name,
                    groupId: group.id,
                    groupName: group.name,
                    instanceIndex: group.repeats ? index + 1 : undefined,
                    instanceNumber: instance.instanceNumber,
                };
                for (const field of sortedFields(group)) {
                    if (!isEditable(field, instance)) continue;
                    if (isMissing(field, instance)) {
                        missing.push({ ...context, fieldId: field.id, fieldLabel: field.label });
                    }
                    for (const { option, instance: subInstance } of activeSubforms(field, recordValue(instance, field.id), state.subforms)) {
                        for (const subField of sortedFields(option.subformGroup).filter(f => isMissing(f, subInstance))) {
                            missing.push({ ...context, fieldId: subField.id, fieldLabel: subField.label, subformOf: field.label, triggerOptionId: option.id });
                        }
                    }
                }
            });
        }
    }
    return missing;
}

// ------------------------------------------------------------ forwarded options

/**
 * `forwardedFrom: <field name>` — the field's options are the values currently held by
 * another field (e.g. a lesion's `lesion_diagnoses` choose among the visit's
 * `suspected_diagnoses`). The source is read from the edited state first, then from
 * the stored submission; entries are keyed by `forwardedValueKey` (default multioption).
 * Without this the web renders such a field with no options, so a required one can
 * never be filled and the section can never be saved.
 */
export function forwardedOptions(field: any, state: FieldGroupEditState, initData: any): { value: string; label: string }[] | undefined {
    if (!field?.forwardedFrom) return undefined;
    const key = field.forwardedValueKey || 'multioption';
    let source: unknown;
    for (const instances of Object.values(state.instances)) {
        for (const inst of instances) {
            const record = inst.surveyRecords.find(r => r.surveyField?.name === field.forwardedFrom);
            if (record) { source = record.value; break; }
        }
        if (source !== undefined) break;
    }
    if (source === undefined) {
        const stored = (initData?.surveyRecords || []).find((r: any) => r.surveyField?.name === field.forwardedFrom);
        source = stored?.value;
    }
    const parsed = parseStoredFieldValue(source, 'multioption');
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    return parsed
        .map((entry: any) => ({ value: entry?.[key], label: entry?.label ? `${entry[key]} — ${entry.label}` : entry?.[key] }))
        .filter(o => {
            if (typeof o.value !== 'string' || o.value === '' || seen.has(o.value)) return false;
            seen.add(o.value);
            return true;
        });
}

// --------------------------------------------------- instance-selector options

/**
 * The label for a referable instance of the source group: `#<instanceNumber>`, plus the
 * body region when the source group has an answered `lesion_location` field (the NTD eCRF's
 * C-Lesion). `#N` matches how the app labels a lesion; the uuid, not this label, is stored.
 */
function instanceOptionLabel(instance: any, regionField: any): string {
    const base = `#${instance.instanceNumber}`;
    if (!regionField) return base;
    const record = (instance.surveyRecords || []).find(
        (r: any) => r.surveyField?.id === regionField.id || r.surveyField?.name === regionField.name,
    );
    const code = optionValues(record?.value)[0];
    if (!code) return base;
    const option = (regionField.options || []).find((o: any) => o?.value === code);
    return `${base} — ${option?.label || code}`;
}

/**
 * `type: 'instance-selector'`, `instanceSourceGroup: <group name>` (SKIN-687/689) — the
 * field's options are the live instances of another repeating group in the SAME submission,
 * each stored by its stable `groupRecordUuid`. The NTD eCRF's section-D "Applies to lesions"
 * lists the visit's C-Lesion instances. The source group is in another section, so its
 * instances are read from the stored submission; an instance with no `groupRecordUuid` (not
 * yet persisted by the server) cannot be referenced and is left out. Returns [] when the
 * source group is unknown or has no referable instance, undefined when the field is not one.
 */
export function instanceSelectorOptions(field: any, initData: any, sections: any[]): { value: string; label: string }[] | undefined {
    const sourceName = field?.instanceSourceGroup;
    if (!sourceName) return undefined;
    const sourceGroup = (sections || [])
        .flatMap((s: any) => s.fieldGroups || [])
        .find((g: any) => g?.name === sourceName);
    if (!sourceGroup) return [];
    const regionField = (sourceGroup.fields || []).find((f: any) => f.type === 'lesion_location');
    const stored = (initData?.fieldGroups || []).find(
        (fg: any) => fg.fieldGroupId === sourceGroup.id && !fg.triggerOptionId,
    );
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const instance of stored?.instances || []) {
        const uuid = instance?.groupRecordUuid;
        if (typeof uuid !== 'string' || uuid === '' || seen.has(uuid)) continue;
        seen.add(uuid);
        options.push({ value: uuid, label: instanceOptionLabel(instance, regionField) });
    }
    return options;
}
